"use client";

import { useState } from "react";
import Link from "next/link";
import { Dialog, DialogPanel, DialogTitle, Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import Logo from "@/components/Logo";
import Button from "@/components/Button";
import { services, site } from "@/content/site";

const navLink = "text-sm/6 font-semibold text-neutral-900 hover:text-brand-600";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const closeMobile = () => setMobileMenuOpen(false);

  return (
    <>
      <a
        href="#main"
        className="sr-only z-60 rounded-control bg-white px-4 py-2 text-sm font-semibold text-brand-700 focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Skip to content
      </a>
      <header className="absolute inset-x-0 top-0 z-50">
        <nav aria-label="Global" className="flex items-center justify-between p-6 lg:px-8">
          <div className="flex lg:flex-1">
            <Link href="/" className="-m-1.5 p-1.5" aria-label="Dawsco home">
              <Logo />
            </Link>
          </div>
          <div className="flex lg:hidden">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="-m-2.5 inline-flex items-center justify-center rounded-control p-2.5 text-neutral-700"
            >
              <span className="sr-only">Open main menu</span>
              <Bars3Icon aria-hidden="true" className="size-6" />
            </button>
          </div>
          <div className="hidden lg:flex lg:items-center lg:gap-x-12">
            <Popover className="relative">
              <PopoverButton className={`inline-flex items-center gap-x-1 ${navLink}`}>
                <span>Services</span>
                <ChevronDownIcon aria-hidden="true" className="size-5" />
              </PopoverButton>
              <PopoverPanel
                transition
                className="absolute left-1/2 z-10 mt-5 flex w-screen max-w-max -translate-x-1/2 bg-transparent px-4 transition data-closed:translate-y-1 data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in"
              >
                {({ close }) => (
                  <div className="w-screen max-w-sm flex-auto rounded-panel bg-white p-4 text-sm/6 shadow-lg outline-1 outline-neutral-900/5">
                    {services.map((item) => (
                      <div key={item.slug} className="relative rounded-card p-4 hover:bg-neutral-50">
                        <Link href={item.href} onClick={() => close()} className="font-semibold text-neutral-900">
                          {item.label}
                          <span className="absolute inset-0" />
                        </Link>
                        <p className="mt-1 text-neutral-600">{item.description}</p>
                      </div>
                    ))}
                    <div className="relative rounded-card p-4 hover:bg-neutral-50">
                      <Link
                        href={site.allServices.href}
                        onClick={() => close()}
                        className="font-semibold text-brand-600"
                      >
                        {site.allServices.label} <span aria-hidden="true">→</span>
                        <span className="absolute inset-0" />
                      </Link>
                    </div>
                  </div>
                )}
              </PopoverPanel>
            </Popover>
            {site.nav.map((item) => (
              <Link key={item.href} href={item.href} className={navLink}>
                {item.label}
              </Link>
            ))}
          </div>
          <div className="hidden lg:flex lg:flex-1 lg:justify-end">
            <Button href={site.cta.href} size="sm">
              {site.cta.label}
            </Button>
          </div>
        </nav>
        <Dialog open={mobileMenuOpen} onClose={setMobileMenuOpen} className="lg:hidden">
          <div className="fixed inset-0 z-50" />
          <DialogPanel className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white p-6 sm:max-w-sm sm:ring-1 sm:ring-neutral-900/10">
            <DialogTitle className="sr-only">Main menu</DialogTitle>
            <div className="flex items-center justify-between">
              <Link href="/" onClick={closeMobile} className="-m-1.5 p-1.5" aria-label="Dawsco home">
                <Logo />
              </Link>
              <button
                type="button"
                onClick={closeMobile}
                className="-m-2.5 rounded-control p-2.5 text-neutral-700"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="size-6" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-neutral-500/10">
                <div className="space-y-2 py-6">
                  <p className="-mx-3 px-3 py-2 text-base/7 font-semibold text-neutral-900">Services</p>
                  <ul role="list" className="space-y-1 pl-3">
                    {services.map((item) => (
                      <li key={item.slug}>
                        <Link
                          href={item.href}
                          onClick={closeMobile}
                          className="-mx-3 block rounded-card px-3 py-2 text-sm/6 font-semibold text-neutral-700 hover:bg-neutral-50"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                    <li>
                      <Link
                        href={site.allServices.href}
                        onClick={closeMobile}
                        className="-mx-3 block rounded-card px-3 py-2 text-sm/6 font-semibold text-brand-600 hover:bg-neutral-50"
                      >
                        {site.allServices.label}
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="space-y-2 py-6">
                  {site.nav.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={closeMobile}
                      className="-mx-3 block rounded-card px-3 py-2 text-base/7 font-semibold text-neutral-900 hover:bg-neutral-50"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
                <div className="py-6">
                  <Link
                    href={site.cta.href}
                    onClick={closeMobile}
                    className="-mx-3 block rounded-card px-3 py-2.5 text-base/7 font-semibold text-brand-600 hover:bg-neutral-50"
                  >
                    {site.cta.label}
                  </Link>
                </div>
              </div>
            </div>
          </DialogPanel>
        </Dialog>
      </header>
    </>
  );
}
